import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useVisitorRegistrationFormSubmitDialog } from '../../../../hooks/useDialog'
import Dialog from '../../../atoms/feedback/Dialog'
import { FlexColumn, FlexRow } from '../../../atoms/layout/Flex'
import Label from '../../../atoms/display/Label'
import { VisitorRegistrationFormSubcontractor, VisitorRegistrationFormWorker } from '../../../../types/Company'
import Button from '../../../atoms/inputs/Button'
import { CompanyActions } from '../../../../store/company'
import { CompanySelectors } from '../../../../store/company/selectors'
import VisitorRegistrationWorkerSelectDialog from './worker/SelectDialog'
import Alert from '../../../atoms/feedback/Alert'
import VisitorRegistrationSubcontractorSelectDialog from './subcontractor/SelectDialog'

export default function VisitorRegistrationSubmitDialog() {
  const { state, close } = useVisitorRegistrationFormSubmitDialog()
  const form = useSelector(CompanySelectors.visitorRegistrationForm)
  const workers = useSelector(CompanySelectors.visitorRegistrationFormWorkers)
  const subcontractors = useSelector(CompanySelectors.visitorRegistrationFormSubcontractors)
  const [workerSelectDialog, setWorkerSelectDialog] = useState(false)
  const [subcontractorSelectDialog, setSubcontractorSelectDialog] = useState(false)
  const [selectedWorkers, setSelectedWorkers] = useState<VisitorRegistrationFormWorker[]>([])
  const [selectedSubcontractors, setSelectedSubcontractors] = useState<VisitorRegistrationFormSubcontractor[]>([])
  const dispatch = useDispatch()
  const disableSubmit = form == null || form.answers == null

  useEffect(() => {
    dispatch(CompanyActions.fetchVisitorRegistrationForm())
    dispatch(CompanyActions.fetchVisitorRegistrationFormWorkers())
    dispatch(CompanyActions.fetchVisitorRegistrationFormSubcontractors())
  }, [dispatch])

  const handleSubmit = useCallback(() => {
    if (state) {
      dispatch(
        CompanyActions.createVisitorRegistrationRecord({
          clientId: state.client.id,
          workerIds: selectedWorkers.map((worker) => worker.id),
          subcontractorIds: selectedSubcontractors.map((subcontractor) => subcontractor.id),
        })
      )
    }
  }, [dispatch, state, selectedWorkers, selectedSubcontractors])

  return (
    <Dialog
      open={state !== undefined}
      onClose={close}
      title="新規入場者調査票の提出"
      actions={
        <Button disabled={disableSubmit} onClick={handleSubmit}>
          提出する
        </Button>
      }
    >
      <FlexColumn>
        {state && (
          <FlexRow>
            <Label text="提出先" />
            <Label text={state.client.name} />
          </FlexRow>
        )}
        {form == null && (
          <Alert severity="error" title="事業者情報とアンケートが登録されていません">
            新規入場者調査票を提出するには、事業者情報とアンケートを登録する必要があります
          </Alert>
        )}
        {form && form.answers == null && (
          <Alert severity="error" title="アンケートが登録されていません">
            新規入場者調査票を提出するには、アンケートを登録する必要があります
          </Alert>
        )}
        <FlexRow align="center">
          <Label text="労働者情報" variant="subtitle" />
          <Button height="small" size="sm" onClick={() => setWorkerSelectDialog(true)}>
            選択
          </Button>
          <VisitorRegistrationWorkerSelectDialog
            open={workerSelectDialog}
            onClose={() => setWorkerSelectDialog(false)}
            workers={workers}
            selectedWorkers={selectedWorkers}
            onSelect={setSelectedWorkers}
          />
        </FlexRow>
        {selectedWorkers.length === 0 && <Alert severity="info">労働者は選択されていません</Alert>}
        {selectedWorkers.map((worker) => (
          <FlexRow ml={2}>
            <Label text={worker.name} />
          </FlexRow>
        ))}
        <FlexRow align="center">
          <Label text="下請け情報" variant="subtitle" />
          <Button height="small" size="sm" onClick={() => setSubcontractorSelectDialog(true)}>
            選択
          </Button>
          <VisitorRegistrationSubcontractorSelectDialog
            open={subcontractorSelectDialog}
            onClose={() => setSubcontractorSelectDialog(false)}
            subcontractors={subcontractors}
            selectedSubcontractors={selectedSubcontractors}
            onSelect={setSelectedSubcontractors}
          />
        </FlexRow>
        {selectedSubcontractors.length === 0 && <Alert severity="info">下請けは選択されていません</Alert>}
        {selectedSubcontractors.map((subcontractor) => (
          <FlexRow ml={2}>
            <Label text={subcontractor.name} />
          </FlexRow>
        ))}
        <Alert severity="warning">
          <FlexColumn space={0}>
            <Label text="提出すると、代表者がアンケートに回答したものとみなされます" />
            <Label text="調査票の個人情報は、安全衛生管理及び緊急時の連絡・対応のために使用されます" />
          </FlexColumn>
        </Alert>
      </FlexColumn>
    </Dialog>
  )
}
